import { match, P } from 'ts-pattern';

import { SESSION_GENERATE_ACTION, type SubscriptionMe } from '@/entities/subscription';
import { formatRelativeDays, formatShortDate } from '@/shared/lib/time-utils';

import type { BillingStatus } from './get-billing-status';

const deletionWarning = (retentionEndsAt: string | null, reason: string) =>
    `Your account will be deleted${
        retentionEndsAt ? ` ${formatRelativeDays(retentionEndsAt)}` : ''
    } ${reason}`;

export const formatBillingFooterLine = (
    status: BillingStatus,
    {
        planCode,
        nextPlanCode,
        limits,
        currentPeriodEnd,
        retentionEndsAt,
    }: Pick<
        SubscriptionMe,
        'planCode' | 'nextPlanCode' | 'limits' | 'currentPeriodEnd' | 'retentionEndsAt'
    >,
    // null keeps its existing meaning — the catalog does not list the incoming plan, so its size
    // is unknown. A plan that sets no cap is a different fact, and says so.
    scheduledPlanSessions: number | 'unlimited' | null,
): string =>
    match(status)
        .with('active', () => {
            // The line describes the plan in effect after the next billing date — the current one
            // on a straight renewal, the incoming one when a change is scheduled. An incoming plan
            // the catalog cannot size drops the count rather than borrowing the current plan's
            // number, which would name a plan that is not starting.
            const hasScheduledPlanChange = nextPlanCode !== planCode;
            const nextPeriodSessions = hasScheduledPlanChange
                ? scheduledPlanSessions
                : (limits[SESSION_GENERATE_ACTION]?.limit ?? 'unlimited');
            const planLabel = match(nextPeriodSessions)
                .with(P.number, (sessions) => `${sessions} session plan`)
                .with('unlimited', () => 'Unlimited sessions plan')
                .with(null, () => 'Session plan')
                .exhaustive();
            const nextPeriodVerb = hasScheduledPlanChange ? 'starts' : 'renews';

            return currentPeriodEnd
                ? `${planLabel} ${nextPeriodVerb} ${formatShortDate(currentPeriodEnd)}`
                : planLabel;
        })
        .with('cancelling', () =>
            currentPeriodEnd
                ? `Reactivate plan to continue using Nesso after ${formatShortDate(currentPeriodEnd)}`
                : 'Reactivate plan to continue using Nesso',
        )
        .with('canceled', () => deletionWarning(retentionEndsAt, 'without an active plan'))
        .with('past-due', () => deletionWarning(retentionEndsAt, 'without a valid payment method'))
        .exhaustive();
